import { AfterViewInit, Component, OnInit, ViewChild, Inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { CUSTOMER_ENTITY_SERVICE_TOKEN } from '../../../injection-tokens/entity-service-tokens';
import { ICustomerEntityService } from '../../../interfaces/entities/customer-entity-service.interface';
import { CustomerEntity } from '../../../models/entities/customer';
import { NewCustomerDrawerComponent } from '../../shared/new-customer-drawer/new-customer-drawer.component';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import {
  DEFAULT_PAGINATOR_PAGE_SIZE,
  PAGINATOR_PAGE_SIZE_OPTIONS,
} from '../../../shared/pagination-options';

@SidebarNavMenuItem({
  route: '/customer-add-edit',
  faIcon: 'groups',
  name: 'Customer Management',
  order: 1200,
  businessProcessesName: 'Customers',
})
@Component({
  selector: 'app-customer-create-edit',
  templateUrl: './customer-create-edit.component.html',
  styleUrls: ['./customer-create-edit.component.css']
})
export class CustomerCreateEditComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('newCustomerDrawer') newCustomerDrawer!: NewCustomerDrawerComponent;

  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  dataSource = new MatTableDataSource<CustomerEntity>([]);
  displayedColumns = ['name', 'gstNumber', 'email', 'phone', 'updatedOn', 'actions'];

  loading = false;
  saving = false;
  searchTerm = '';
  customerNameTaken = false;
  customerNameChecking = false;
  allCustomers: CustomerEntity[] = [];

  // New Customer drawer (shared component)
  newCustomerDrawerOpen = false;

  // Sidenav drawer (detail and edit views)
  drawerOpen = false;
  drawerMode: 'detail' | 'form' = 'detail';
  formMode: 'add' | 'edit' = 'add';

  selectedCustomer: CustomerEntity | null = null;
  formCustomer: CustomerEntity = this.createEmptyCustomer();

  constructor(
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN)
    private readonly customerService: ICustomerEntityService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly notification: NotificationService,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.dataSource.filterPredicate = (row, filter) => {
      const normalized = filter.trim().toLowerCase();
      const email = row.contactPerson?.email ?? '';
      const phone = row.contactPerson?.mobile ?? '';
      return [row.name, row.gstNumber, email, phone].join(' ').toLowerCase().includes(normalized);
    };

    this.loadCustomers();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item: CustomerEntity, property: string): string | number => {
      switch (property) {
        case 'email':
          return (item.contactPerson?.email ?? '').toLowerCase();
        case 'phone':
          return (item.contactPerson?.mobile ?? '').toLowerCase();
        case 'updatedOn':
          return item.updatedOn ? new Date(item.updatedOn).getTime() : 0;
        default:
          return (item as any)[property] ?? '';
      }
    };
    this.dataSource.paginator = this.paginator;
  }

  get canArchive(): boolean {
    return this.authService.canArchive();
  }

  async loadCustomers(): Promise<void> {
    this.loading = true;
    try {
      const customers = await this.customerService.getAllCustomers();
      this.allCustomers = customers;
      this.dataSource.data = customers;
      this.applySearchFilter();
    } catch {
      this.notification.error('Failed to load customers. Please try again.', 4000);
    } finally {
      this.loading = false;
    }
  }

  onSearchChange(): void {
    this.applySearchFilter();
  }

  private applySearchFilter(): void {
    this.dataSource.filter = this.searchTerm.toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onNameClick(row: CustomerEntity): void {
    this.openDetailDrawer(row.id);
  }

  onRowClick(row: CustomerEntity): void {
    this.openDetailDrawer(row.id);
  }

  openAddDrawer(): void {
    this.customerNameTaken = false;
    this.customerNameChecking = true;
    this.newCustomerDrawerOpen = true;

    // Refresh customer list so duplicate check reflects latest data
    this.customerService.getAllCustomers()
      .then(all => {
        this.allCustomers = all;
      })
      .finally(() => {
        this.customerNameChecking = false;
      });
  }

  openEditDrawer(row: CustomerEntity): void {
    this.formMode = 'edit';
    this.drawerMode = 'form';
    this.customerNameTaken = false;
    this.customerNameChecking = false;
    this.drawerOpen = true;

    this.customerService.getCustomerByIdRemote(row.id).subscribe({
      next: customer => {
        this.formCustomer = this.cloneCustomer(customer);
        this.onCustomerNameInput(this.formCustomer.name);
      },
      error: () => {
        this.notification.error('Failed to load customer details.', 3500);
        this.closeDrawer();
      }
    });
  }

  private openDetailDrawer(id: string): void {
    this.drawerMode = 'detail';
    this.drawerOpen = true;
    this.selectedCustomer = null;

    this.customerService.getCustomerByIdRemote(id).subscribe({
      next: customer => {
        this.selectedCustomer = customer;
      },
      error: () => {
        this.notification.error('Failed to load customer details.', 3500);
        this.closeDrawer();
      }
    });
  }

  saveCustomer(): void {
    if (!this.isValidFormCustomer()) {
      this.notification.warning('Name is required.', 2500);
      return;
    }

    if (this.customerNameTaken) {
      this.notification.warning('Name already exists. Please choose a different customer name.', 2500);
      return;
    }

    this.saving = true;
    const payload = this.cloneCustomer(this.formCustomer);

    if (this.formMode === 'add') {
      this.customerService.createCustomer(payload).subscribe({
        next: () => {
          this.saving = false;
          this.notification.success('Customer created successfully.', 3000);
          this.closeDrawer();
          this.loadCustomers();
        },
        error: () => {
          this.saving = false;
          this.notification.error('Failed to create customer.', 3500);
        }
      });
      return;
    }

    this.customerService.updateCustomerRemote(payload.id, payload).subscribe({
      next: () => {
        this.saving = false;
        this.notification.success('Customer updated successfully.', 3000);
        this.closeDrawer();
        this.loadCustomers();
      },
      error: () => {
        this.saving = false;
        this.notification.error('Failed to update customer.', 3500);
      }
    });
  }

  archiveCustomer(row: CustomerEntity): void {
    this.confirmDialog.confirmAction(
      'Archive Customer',
      `Archive "${row.name}"? Archived customers are hidden from the list.`,
      'warning',
      'Archive',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.customerService.archiveCustomer(row.id).subscribe({
        next: () => {
          this.notification.success(`Customer "${row.name}" archived.`, 3000);
          if (this.selectedCustomer?.id === row.id) {
            this.closeDrawer();
          }
          this.loadCustomers();
        },
        error: () => {
          this.notification.error('Failed to archive customer.', 3500);
        }
      });
    });
  }

  closeDrawer(): void {
    this.drawerOpen = false;
    this.saving = false;
    this.selectedCustomer = null;
    this.formCustomer = this.createEmptyCustomer();
    this.customerNameTaken = false;
    this.customerNameChecking = false;
  }

  // ── New Customer Drawer (shared component) ──────────────────────────────────
  closeNewCustomerDrawer(): void {
    this.newCustomerDrawerOpen = false;
    this.customerNameTaken = false;
    this.customerNameChecking = false;
  }

  onNewCustomerNameChange(value: string): void {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      this.customerNameTaken = false;
      this.customerNameChecking = false;
      return;
    }

    this.customerNameTaken = this.isDuplicateCustomerName(trimmed);
    this.customerNameChecking = false;
  }

  onNewCustomerSaved(entity: CustomerEntity): void {
    this.upsertCustomer(entity);
    this.dataSource.data = [...this.allCustomers];
    this.applySearchFilter();
    this.notification.success(`Customer "${entity.name}" added.`, 3000);
  }

  private upsertCustomer(customer: CustomerEntity): void {
    const id = (customer.id ?? '').trim();
    const index = this.allCustomers.findIndex(c => (c.id ?? '').trim() === id);
    if (index === -1) {
      this.allCustomers.push(customer);
    } else {
      this.allCustomers[index] = customer;
    }
  }

  onCustomerNameInput(value: string): void {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      this.customerNameTaken = false;
      this.customerNameChecking = false;
      return;
    }

    this.customerNameChecking = true;
    this.customerNameTaken = this.isDuplicateCustomerName(trimmed);
    this.customerNameChecking = false;
  }

  isSaveButtonDisabled(): boolean {
    return this.saving || !this.formCustomer.name?.trim() || this.customerNameTaken || this.customerNameChecking;
  }

  private isDuplicateCustomerName(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    return this.allCustomers.some(c => {
      const existing = (c.name ?? '').trim().toLowerCase();
      if (existing !== normalized) {
        return false;
      }

      if (this.formMode === 'edit') {
        return c.id !== this.formCustomer.id;
      }

      return true;
    });
  }

  private isValidFormCustomer(): boolean {
    return !!this.formCustomer.name?.trim();
  }

  getCustomerEmail(customer: CustomerEntity | null): string {
    return customer?.contactPerson?.email || '—';
  }

  getCustomerPhone(customer: CustomerEntity | null): string {
    return customer?.contactPerson?.mobile || '—';
  }

  private createEmptyCustomer(): CustomerEntity {
    return new CustomerEntity();
  }

  private cloneCustomer(source: CustomerEntity): CustomerEntity {
    const cloned = new CustomerEntity();
    cloned.id = source.id;
    cloned.name = source.name;
    cloned.address = source.address;
    cloned.website = source.website;
    cloned.gstNumber = source.gstNumber;
    cloned.contactPerson.id = source.contactPerson?.id || '';
    cloned.contactPerson.name = source.contactPerson?.name || '';
    cloned.contactPerson.email = source.contactPerson?.email || '';
    cloned.contactPerson.mobile = source.contactPerson?.mobile || '';
    cloned.updatedOn = source.updatedOn;
    cloned.createdOn = source.createdOn;
    cloned.isActive = source.isActive;
    return cloned;
  }
}
